import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFetcher } from 'react-router';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import states from 'data/states';
import AddressAutocomplete from 'features/account/AddressAutocomplete';
import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';
import Select from 'honeydew/Select/';
import TextField from 'honeydew/TextField';
import useStateSelect from 'hooks/useStateSelect';
import profileStyles from 'pages/account/Profile/styles';
import AddressVerificationModal from 'shared/AddressVerificationModal';
import { isInvalidStreetAddress } from 'utils/addressUtils';
import logError from 'utils/errorUtils';
import { capitalizeWords } from 'utils/globalUtils';
import {
    checkIsPOBox,
    getHasFootnote,
    mapSmartyCandidateAddressToJalapeno,
    smartyStreetAddressLookup,
} from 'utils/smartyStreetUtils';

const mobileHeading = <h2 className="text-body-lg mb-20 font-grotesk-bold lg:hidden">Shipping Address</h2>;

export default function ShippingAddress({ setPageAlert }) {
    const { data: account } = useAccountQuery();
    const { data: subscription } = useSubscriptionQuery();
    const address = subscription?.shippingAddr ?? {};
    const { isDeliveredByImperfect } = account;
    const { address1, address2, city, deliveryInstructions, province, zip } = address;
    const [isEditing, setIsEditing] = useState(false);

    const handleOpenDeliveryInstructions = () => {
        setIsEditing(true);

        setTimeout(() => {
            const element = document.getElementById('deliveryInstructions');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 200);
    };

    return isEditing ? (
        <div className={profileStyles.editContainer}>
            {mobileHeading}
            <Form
                address={address}
                ebtCard={subscription?.ebtCard}
                isDeliveredByImperfect={isDeliveredByImperfect}
                marketplaceOpen={account.marketplaceOpen}
                setIsEditing={setIsEditing}
                setPageAlert={setPageAlert}
                subscriptionID={subscription.id}
            />
        </div>
    ) : (
        <div className={profileStyles.displayContainer}>
            <div>
                {mobileHeading}
                <p>
                    {capitalizeWords(address1)} {capitalizeWords(address2)}
                </p>
                <p>
                    {capitalizeWords(city)}, {province} {zip}
                </p>
                {isDeliveredByImperfect && deliveryInstructions && (
                    <>
                        <h4 className="mt-20 text-grey uppercase">Delivery Instructions</h4>
                        <p>{deliveryInstructions}</p>
                    </>
                )}
                {isDeliveredByImperfect && !deliveryInstructions && (
                    <button
                        type="button"
                        onClick={handleOpenDeliveryInstructions}
                        className="link text-body-sm mt-20 flex items-center"
                    >
                        + Add delivery instructions
                    </button>
                )}
            </div>
            <button
                type="button"
                onClick={() => setIsEditing(true)}
                className={`${profileStyles.edit} self-start lg:self-center`}
            >
                Edit
            </button>
        </div>
    );
}

ShippingAddress.propTypes = {
    setPageAlert: PropTypes.func.isRequired,
};

function Form({
    address,
    ebtCard,
    isDeliveredByImperfect,
    marketplaceOpen,
    setIsEditing,
    setPageAlert,
    subscriptionID,
}) {
    const fetcher = useFetcher();
    const { address1, address2, city, deliveryInstructions, province, zip, ...addressFields } = address;
    const isBusy = fetcher.state !== 'idle' && fetcher.formData !== undefined;
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [showPOBoxError, setShowPOBoxError] = useState(false);

    // Address verification state
    const [showAddressVerificationModal, setShowAddressVerificationModal] = useState(false);
    const [providedAddress, setProvidedAddress] = useState(null);
    const [suggestedAddress, setSuggestedAddress] = useState(null);
    const addressVerificationDeferredRef = useRef(null);

    // Address field state
    const [address1Field, setAddress1Field] = useState(address1 ?? '');
    const [address2Field, setAddress2Field] = useState(address2 ?? '');
    const [cityField, setCityField] = useState(city ?? '');
    const [stateField, setStateField] = useState(province ?? '');
    const [zipField, setZipField] = useState(zip ?? '');

    const stateSelect = useStateSelect(states, stateField);

    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data?.success) {
            setIsEditing(false);
            setPageAlert(marketplaceOpen ? 'success.shipping.customize' : 'success.shipping');
            window.scroll({
                top: 0,
                behavior: 'smooth',
            });
        }

        setShowErrorAlert(fetcher.state === 'idle' && fetcher.data?.error);
    }, [fetcher]);

    async function addressVerificationModal(formAddress, smartyAddress) {
        const promise = new Promise((resolve, reject) => {
            addressVerificationDeferredRef.current = {
                resolve,
                reject,
            };
        });
        setProvidedAddress(formAddress);
        setSuggestedAddress(mapSmartyCandidateAddressToJalapeno(smartyAddress));
        setShowAddressVerificationModal(true);
        return promise;
    }

    const handleChangeZip = (e) => {
        const { value } = e.target;
        if (/^[0-9]*$/.test(value) && value.length <= 5) {
            setZipField(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setShowPOBoxError(false);

        const formData = new FormData(e.currentTarget);
        const formAddress = Object.fromEntries(formData);

        if (isInvalidStreetAddress({ street: formAddress.address1 })) {
            formData.append('addressError', true);
            return fetcher.submit(formData, { method: 'POST', action: '/account/profile' });
        }

        // Smarty streets verification
        try {
            const smartyResponse = await smartyStreetAddressLookup(formAddress);
            const [smartyAddress] = smartyResponse?.lookups[0]?.result ?? null;

            const isPOBox = checkIsPOBox({ smartyAddress, formAddress });

            if (isPOBox) {
                setShowPOBoxError(true);
                return undefined;
            }

            const hasFootnote = getHasFootnote({ footnotes: smartyAddress?.analysis?.footnotes });

            if (smartyAddress && hasFootnote) {
                const customerResponse = await addressVerificationModal(formAddress, smartyAddress);

                switch (customerResponse.choice) {
                    case 'keepAddress':
                        break;
                    case 'useSuggestedAddress':
                        Object.keys(customerResponse?.address).forEach((key) =>
                            formData.set(key, customerResponse.address[key])
                        );
                        break;
                    case 'cancel':
                        return false;
                    default:
                        throw new Error('Unknown customerResponse for AddressVerificationModal.');
                }
            }
        } catch (err) {
            const errorMessage = err?.error ?? err;
            logError(errorMessage);
            console.error(errorMessage);
        }

        return fetcher.submit(formData, { method: 'POST', action: '/account/profile' });
    };

    return (
        <>
            <form action="." method="POST" onSubmit={handleSubmit}>
                <fieldset className="flex flex-col gap-5">
                    <AddressAutocomplete
                        inputValue={address1Field}
                        setInputValue={setAddress1Field}
                        setAddressSelection={(value) => {
                            setAddress1Field(value?.streetLine ?? '');
                            setAddress2Field('');
                            setCityField(value?.city ?? '');
                            setStateField(value?.state ?? '');
                            setZipField(value?.zipcode ?? '');
                        }}
                    />

                    {/* TODO: this is inititally a link that reveals the field in the Figma */}
                    <TextField
                        value={capitalizeWords(address2Field)}
                        onChange={(e) => setAddress2Field(e.target.value)}
                        id="address2"
                        label="Apt, Suite, etc"
                        name="address2"
                    />
                    <div className="grid gap-10 lg:grid-cols-2">
                        <TextField
                            id="city"
                            name="city"
                            value={capitalizeWords(cityField)}
                            onChange={(e) => setCityField(e.target.value)}
                            label="City"
                            required
                        />
                        <TextField
                            id="zip"
                            name="zip"
                            value={zipField}
                            maxLength={5}
                            label="Zip"
                            type="number"
                            pattern="[0-9]*"
                            required
                            onChange={handleChangeZip}
                        />
                    </div>
                    <div className="my-10">
                        <Select {...stateSelect} required />
                    </div>
                    <div id="deliveryInstructions">
                        {isDeliveredByImperfect && (
                            <>
                                <TextField
                                    multiline
                                    id="delivery-instructions"
                                    rows={5}
                                    name="deliveryInstructions"
                                    defaultValue={deliveryInstructions}
                                    label="Delivery Instructions"
                                    maxLength={300}
                                    placeholder="For example: ring doorbell, leave package by the side door..."
                                />
                                <p className="text-body-desktop-xs text-grey">
                                    Delivery instructions will be saved and associated with this shipping address. They
                                    will be applied to any future orders that are being delivered via a compatible
                                    carrier.{' '}
                                </p>
                            </>
                        )}
                        <p className="text-body-sm my-15 font-grotesk-bold">
                            {marketplaceOpen ? (
                                <>
                                    Heads up! Your cart will be cleared{' '}
                                    {ebtCard?.last4 ? 'and any EBT payments made may be refunded' : ''} if you change
                                    your address during an open shopping window.
                                </>
                            ) : (
                                <>
                                    Heads up! Any updates will apply to future orders only. Your past orders, where the
                                    shopping window has already ended, will not be affected.
                                </>
                            )}
                        </p>

                        <input type="hidden" defaultValue={subscriptionID} name="subscriptionID" />
                        <input type="hidden" defaultValue="updateAddress" name="_action" />
                        {/* NOTE: address fields that were not destructured above will be passed back to the API untouched here. */}
                        {Object.keys(addressFields).map((key) => (
                            <input key={key} type="hidden" name={key} value={addressFields[key] ?? ''} />
                        ))}
                        {showErrorAlert && (
                            <div className="mt-15">
                                <Alert variant="error" onClose={() => setShowErrorAlert(false)}>
                                    {fetcher?.data?.message || 'Unable to update shipping address.'}
                                </Alert>
                            </div>
                        )}
                        {showPOBoxError && (
                            <div className="mt-15">
                                <Alert variant="error" onClose={() => setShowPOBoxError(false)}>
                                    We can&apos;t deliver to PO Boxes. Please enter another address.
                                </Alert>
                            </div>
                        )}
                        <div className="mt-15">
                            <Button size="lg" type="submit" disabled={isBusy}>
                                {isBusy ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                        <button type="button" onClick={() => setIsEditing()} className={profileStyles.cancel}>
                            Cancel
                        </button>
                    </div>
                </fieldset>
            </form>
            {showAddressVerificationModal && (
                <AddressVerificationModal
                    headline="Verify your address"
                    onClose={() => setShowAddressVerificationModal(false)}
                    providedAddress={providedAddress}
                    suggestedAddress={suggestedAddress}
                    handleVerification={addressVerificationDeferredRef.current.resolve}
                />
            )}
        </>
    );
}

Form.propTypes = {
    address: PropTypes.shape({}).isRequired,
    ebtCard: PropTypes.shape({ last4: PropTypes.string }),
    isDeliveredByImperfect: PropTypes.bool.isRequired,
    marketplaceOpen: PropTypes.bool.isRequired,
    setIsEditing: PropTypes.func.isRequired,
    setPageAlert: PropTypes.func.isRequired,
    subscriptionID: PropTypes.number.isRequired,
};

Form.defaultProps = {
    ebtCard: null,
};
